/**
 * React Query hooks for company data
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { api, type JobsQueryOptions } from '../services/api';
import type { ChatRequest } from '../types';

// Query keys
export const companyKeys = {
  all: ['companies'] as const,
  list: () => [...companyKeys.all, 'list'] as const,
  detail: (id: string) => [...companyKeys.all, 'detail', id] as const,
  overview: (id: string) => [...companyKeys.all, 'overview', id] as const,
  comparison: (ids: string[]) => [...companyKeys.all, 'comparison', ...ids.sort()] as const,
  news: (id: string, filters?: Record<string, unknown>) =>
    [...companyKeys.all, 'news', id, filters] as const,
  products: (id: string) => [...companyKeys.all, 'products', id] as const,
  kbStatus: (id: string) => [...companyKeys.all, 'kb-status', id] as const,
  stock: (id: string, period: string) => [...companyKeys.all, 'stock', id, period] as const,
  quarterlyRevenue: (id: string) => [...companyKeys.all, 'quarterly-revenue', id] as const,
  // Spike 13: Jobs
  jobs: (id: string, options?: JobsQueryOptions) =>
    [...companyKeys.all, 'jobs', id, options] as const,
  hiringMetrics: (id: string) => [...companyKeys.all, 'hiring-metrics', id] as const,
  hiringSignals: (id: string) => [...companyKeys.all, 'hiring-signals', id] as const,
};

export const reportKeys = {
  all: ['reports'] as const,
  templates: () => [...reportKeys.all, 'templates'] as const,
  template: (type: string) => [...reportKeys.all, 'template', type] as const,
};

// List companies
// Pass enabled: true only when authenticated to avoid 401 errors on startup
export function useCompanies(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: companyKeys.list(),
    queryFn: api.listCompanies,
    enabled: options?.enabled ?? true,
  });
}

// Get company detail
export function useCompany(companyId: string) {
  return useQuery({
    queryKey: companyKeys.detail(companyId),
    queryFn: () => api.getCompany(companyId),
    enabled: !!companyId,
  });
}

// Get company overview
export function useCompanyOverview(companyId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: companyKeys.overview(companyId),
    queryFn: () => api.getCompanyOverview(companyId),
    enabled: !!companyId && (options?.enabled ?? true),
  });
}

// Get company news
export function useCompanyNews(
  companyId: string,
  options?: {
    days?: number;
    materiality?: string[];
    limit?: number;
    offset?: number;
  }
) {
  return useQuery({
    queryKey: companyKeys.news(companyId, options),
    queryFn: () => api.getCompanyNews(companyId, options),
    enabled: !!companyId,
  });
}

// Get company products
export function useCompanyProducts(companyId: string) {
  return useQuery({
    queryKey: companyKeys.products(companyId),
    queryFn: () => api.getCompanyProducts(companyId),
    enabled: !!companyId,
  });
}

// Spike 13: Get company portfolio with subsidiaries
export function useCompanyPortfolio(companyId: string) {
  return useQuery({
    queryKey: [...companyKeys.all, 'portfolio', companyId] as const,
    queryFn: () => api.getCompanyPortfolio(companyId),
    enabled: !!companyId,
  });
}

// Spike 13: Get product competitors
export function useProductCompetitors(productId: string, limit: number = 5) {
  return useQuery({
    queryKey: ['products', 'competitors', productId, limit] as const,
    queryFn: () => api.getProductCompetitors(productId, limit),
    enabled: !!productId,
  });
}

// Spike 13: Get competitive feature matrix
export function useCompetitiveMatrix(productId: string, competitorId: string) {
  return useQuery({
    queryKey: ['products', 'competitive-matrix', productId, competitorId] as const,
    queryFn: () => api.getCompetitiveMatrix(productId, competitorId),
    enabled: !!productId && !!competitorId,
  });
}

// Get KB status
export function useKBStatus(companyId: string) {
  return useQuery({
    queryKey: companyKeys.kbStatus(companyId),
    queryFn: () => api.getKBStatus(companyId),
    enabled: !!companyId,
    // PERF: Only poll when KB is building to avoid unnecessary network requests
    refetchInterval: (query) => {
      const isBuilding = query.state.data?.is_building;
      return isBuilding ? 5000 : false; // Poll every 5s while building, stop when done
    },
  });
}

// Chat mutation
export function useChat() {
  return useMutation({
    mutationFn: (request: ChatRequest) => api.sendChatMessage(request),
  });
}

// Conversation query keys
export const conversationKeys = {
  all: ['conversations'] as const,
  list: (companyId?: string) => [...conversationKeys.all, 'list', companyId] as const,
  detail: (id: string) => [...conversationKeys.all, 'detail', id] as const,
};

// Conversations hooks
export function useConversations(companyId?: string, enabled: boolean = true) {
  return useQuery({
    queryKey: conversationKeys.list(companyId),
    queryFn: () => api.listConversations(companyId),
    enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useConversation(conversationId: string | null, enabled: boolean = true) {
  return useQuery({
    queryKey: conversationKeys.detail(conversationId ?? ''),
    queryFn: () => api.getConversation(conversationId!),
    enabled: !!conversationId && enabled,
  });
}

export function useDeleteConversation() {
  return useMutation({
    mutationFn: (conversationId: string) => api.deleteConversation(conversationId),
  });
}

// Stock data (public companies only)
export function useStockData(companyId: string, period: string = '1mo', enabled: boolean = true) {
  return useQuery({
    queryKey: companyKeys.stock(companyId, period),
    queryFn: () => api.getStockData(companyId, period),
    enabled: !!companyId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Quarterly revenue (public companies only)
export function useQuarterlyRevenue(companyId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: companyKeys.quarterlyRevenue(companyId),
    queryFn: () => api.getQuarterlyRevenue(companyId),
    enabled: !!companyId && enabled,
    staleTime: 60 * 60 * 1000, // 1 hour (quarterly data doesn't change often)
  });
}

// Report templates
export function useReportTemplates(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: reportKeys.templates(),
    queryFn: api.getReportTemplates,
    enabled: options?.enabled ?? true,
  });
}

// Generate report mutation
export function useGenerateReport() {
  return useMutation({
    mutationFn: ({
      companyId,
      reportType,
      title,
    }: {
      companyId: string;
      reportType: string;
      title?: string;
    }) => api.generateReport(companyId, reportType, title),
  });
}

// Spike 13: Jobs Intelligence hooks
export function useCompanyJobs(companyId: string, options?: JobsQueryOptions) {
  return useQuery({
    queryKey: companyKeys.jobs(companyId, options),
    queryFn: () => api.getCompanyJobs(companyId, options),
    enabled: !!companyId,
  });
}

export function useHiringMetrics(companyId: string) {
  return useQuery({
    queryKey: companyKeys.hiringMetrics(companyId),
    queryFn: () => api.getHiringMetrics(companyId),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useHiringSignals(companyId: string) {
  return useQuery({
    queryKey: companyKeys.hiringSignals(companyId),
    queryFn: () => api.getHiringSignals(companyId),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Company Comparison
export function useCompanyComparison(companyIds: string[], options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: companyKeys.comparison(companyIds),
    queryFn: () => api.compareCompanies(companyIds),
    enabled: companyIds.length >= 2 && companyIds.length <= 3 && (options?.enabled ?? true),
  });
}
